import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Upload,
  Clock,
  Loader,
  AlertCircle,
  CheckCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import marketplaceService from '../../services/marketplace.service';
import useMarketplaceStore from '../../store/marketplaceStore';

const ServiceModal = ({ isOpen, businessId, service, onClose, onSuccess }) => {
  if (!isOpen) return null;
  
  const { 
    productServiceCategories, 
    getProductServiceCategories
  } = useMarketplaceStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Use refs to track if data has been loaded
  const dataLoadedRef = useRef(false);
  const categoriesLoadedRef = useRef(false);
  const allowedLoadedRef = useRef(false);
  
  const [formData, setFormData] = useState({
    title: service?.title || '',
    description: service?.description || '',
    basePrice: service?.basePrice || '',
    deliveryTime: service?.deliveryTime || '',
    images: service?.images || [],
    category: service?.category || ''
  });

  const [errors, setErrors] = useState({});

  // Direct API calls - bypass store completely
  const fetchAllowedCategoriesDirectly = async (businessId) => {
    try {
      const response = await marketplaceAPI.getAllowedProductCategories(businessId);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch allowed categories:', error);
      return [];
    }
  };

  // Single effect to load all data - runs only once when modal opens
  useEffect(() => {
    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true;
      loadAllData();
    }
  }, [businessId]);

  // Effect to set form data when service changes (for editing)
  useEffect(() => {
    if (service) {
      setFormData({
        title: service.title || '',
        description: service.description || '',
        basePrice: service.basePrice || '',
        deliveryTime: service.deliveryTime || '',
        images: service.images || [],
        category: service.category || ''
      });
      
      if (service.category) {
        setSelectedCategory(service.category);
      }
    }
  }, [service]);

  const loadAllData = async () => {
    setLoadingCategories(true);
    try {
      // Load product service categories (only if empty)
      if (productServiceCategories.length === 0 && !categoriesLoadedRef.current) {
        categoriesLoadedRef.current = true;
        await getProductServiceCategories();
      }
      
      // Get allowed categories directly from API - NOT from store
      let allowed = await fetchAllowedCategoriesDirectly(businessId);
      
      // Get current categories from store
      const currentCategories = productServiceCategories.length > 0 
        ? productServiceCategories 
        : useMarketplaceStore.getState().productServiceCategories;
      
      // Filter product service categories to only allowed ones
      const filteredCategories = currentCategories.filter(cat => 
        (allowed || []).includes(cat.id)
      );
      setAvailableCategories(filteredCategories);
      
      // If editing a service, set the selected category
      if (service?.category) {
        setSelectedCategory(service.category);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load service categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    handleInputChange('category', categoryId);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleImageUpload = async (file) => {
    if (formData.images.length >= 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    setUploadingImages(true);
    try {
      const result = await marketplaceService.uploadImage(file);
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, result.data.url].slice(0, 3)
        }));
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to upload image');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Service title is required';
    } else if (formData.title.length < 2) {
      newErrors.title = 'Service title must be at least 2 characters';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Service title must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Service description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (!formData.basePrice || formData.basePrice <= 0) {
      newErrors.basePrice = 'Valid base price is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      const firstError = document.querySelector('.border-red-300');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const submitData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      category: formData.category,
      basePrice: parseFloat(formData.basePrice),
      deliveryTime: formData.deliveryTime?.trim() || null,
      images: formData.images,
      pricingModel: 'FIXED',
      status: 'ACTIVE'
    };

    setIsLoading(true);
    try {
      if (service) {
        await marketplaceAPI.updateService(service.id, submitData);
        toast.success('Service updated successfully');
      } else {
        await marketplaceAPI.createService(businessId, submitData);
        toast.success('Service added successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Service submission error:', error);
      
      if (error.response?.data?.details) {
        const serverErrors = {};
        error.response.data.details.forEach(err => {
          serverErrors[err.field] = err.message;
        });
        setErrors(serverErrors);
        toast.error('Please check the form for errors');
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to save service');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render category selection
  const renderCategorySelection = () => {
    if (loadingCategories) {
      return (
        <div className="flex items-center justify-center py-6">
          <Loader className="h-5 w-5 text-primary-600 animate-spin" />
          <span className="ml-2 text-xs text-gray-500">Loading categories...</span>
        </div>
      );
    }

    if (availableCategories.length === 0) {
      return (
        <div className="bg-yellow-50 rounded-xl p-3 shadow-neomorph-inset border border-yellow-200">
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-yellow-800">No categories available</p>
              <p className="text-[10px] text-yellow-700 mt-0.5">
                Your business doesn't have any service categories assigned yet. Please contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Service Category <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {availableCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryChange(cat.id)}
              className={`p-3 rounded-xl text-left transition-all border ${
                selectedCategory === cat.id
                  ? 'bg-primary-50 border-primary-200 shadow-neomorph-inset'
                  : 'bg-white border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">{cat.icon || '📦'}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800 block truncate">
                    {cat.name}
                  </span>
                  {cat.description && (
                    <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">
                      {cat.description}
                    </p>
                  )}
                </div>
                {selectedCategory === cat.id && (
                  <CheckCircle className="h-4 w-4 text-primary-600 flex-shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>
        {errors.category && (
          <p className="mt-1.5 text-xs text-red-600">{errors.category}</p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-neomorph max-w-2xl w-full my-4 border border-primary-100/30">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  {service ? 'Edit Service' : 'Add Service'}
                </h3>
              </div>
              <p className="text-xs text-gray-500 ml-3 hidden sm:block">
                {service ? 'Update service information' : 'Add a new service to your business'}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto px-1">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-2">Basic Information</h4>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Service Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`block w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                    errors.title ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                  }`}
                  placeholder="e.g., Professional Website Development"
                  maxLength={100}
                />
                {errors.title && (
                  <p className="mt-1.5 text-xs text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Category Selection */}
              {renderCategorySelection()}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className={`block w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                    errors.description ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                  }`}
                  placeholder="Describe your service in detail..."
                  maxLength={1000}
                />
                <div className="flex justify-between mt-1.5 gap-2 flex-wrap">
                  <span className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">
                    {formData.description?.length || 0}/1000
                  </span>
                  {errors.description && (
                    <span className="text-[10px] sm:text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-lg">
                      {errors.description}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-2">Pricing</h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Base Price (₦) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">₦</span>
                    <input
                      type="number"
                      value={formData.basePrice}
                      onChange={(e) => handleInputChange('basePrice', e.target.value)}
                      className={`block w-full pl-7 sm:pl-8 pr-3 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                        errors.basePrice ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                      }`}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {errors.basePrice && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.basePrice}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Delivery Time
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.deliveryTime}
                      onChange={(e) => handleInputChange('deliveryTime', e.target.value)}
                      className="block w-full pl-9 sm:pl-10 pr-3 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      placeholder="e.g., 2-3 days, 1 week"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-2">Service Images</h4>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {formData.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Service ${index + 1}`}
                      className="h-20 w-full rounded-xl object-cover shadow-neomorph-inset"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all opacity-0 group-hover:opacity-100"
                      title="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                
                {formData.images.length < 3 && (
                  <label className="cursor-pointer">
                    <div className="h-20 w-full rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                      {uploadingImages ? (
                        <Loader className="h-5 w-5 text-primary-600 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-5 w-5 text-gray-400" />
                          <span className="text-[9px] sm:text-xs text-gray-500 mt-1">Add</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleImageUpload(file);
                      }}
                      disabled={uploadingImages}
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Up to 3 images. JPG, PNG (max 5MB)
              </p>
            </div>

            {/* Form Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 rounded-xl p-3 sm:p-4 shadow-neomorph-inset border border-red-200/50">
                <div className="flex items-start">
                  <div className="p-1 bg-red-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3">
                    <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[10px] sm:text-xs font-medium text-red-800 mb-1">Please fix the following errors:</h4>
                    <ul className="text-[9px] sm:text-xs text-red-700 space-y-0.5 list-disc list-inside">
                      {Object.values(errors).slice(0, 3).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {Object.keys(errors).length > 3 && (
                        <li>+ {Object.keys(errors).length - 3} more errors</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 pb-6 sm:pb-4 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="order-2 sm:order-1 w-full sm:w-auto bg-white rounded-lg px-4 py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="order-1 sm:order-2 w-full sm:w-auto bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium flex items-center justify-center disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Briefcase className="h-4 w-4 mr-2" />
                  {service ? 'Update' : 'Add Service'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ServiceModal;